import {I18n} from 'aws-amplify/utils';

import {productCopy} from '../../../config/productCopy';
import {
    Drawer,
    DrawerProps,
    ScrollArea,
} from '@mantine/core';

import {RevalidateForm} from '../forms/RevalidateForm';
import {RevalidateFormValues} from '../forms/types';
import {
    useRevalidateProviderConnectionMutation,
    useRotateProviderCredentialMutation,
} from '../hooks/useProviderConnectionsQuery';
import {
    buildRotateInput,
    ProviderConnection,
} from '../types';
import {
    showErrorNotifications,
    showSuccessNotification,
} from '../../../components/ServiceNotifications';

// FNP-1248 / FNP-1269 — THE RE-VALIDATE DRAWER. Step 3 of the three-step flow.
//
// ⚠️ TWO CREDENTIAL TYPES, TWO MUTATIONS, AND THE SPLIT IS THE POINT.
//
//   ASSUMED_ROLE → `revalidateProviderConnection`, which takes ONLY an id.
//                  Nothing about the credential changed: Flexi minted an
//                  external id, the tenant put it in their trust policy, and now
//                  they want Flexi to look again. Routing this through rotate
//                  forced them to retype a role ARN the UI never prefilled, and
//                  a typo in that retype silently repointed the connection at a
//                  DIFFERENT role.
//
//   API_TOKEN    → `rotateProviderCredential`, unchanged. This one really is a
//                  credential change — the tenant is handing over a new secret,
//                  and the `writable` declaration travels with it as before.
//
// ⚠️ THE ASSUMED_ROLE CALL SENDS NO `writable`. Omitting it is what PRESERVES
// the connection's existing declaration; re-sending what the tenant last chose
// would make a re-check capable of revoking a capability.
//
// ⚠️ THIS CLOSES ON SUCCESS, UNLIKE THE CONNECT DRAWER.
//
// The connect drawer stays open because it has something to hand over: a
// freshly-minted external id the tenant has not seen. Neither path here mints
// anything — the connection KEEPS its external id, which is what makes the trust
// policy the tenant already wrote stay correct. There is no new value to
// deliver, so the drawer closes and the detail page behind it shows the updated
// status.
//
// ⚠️ A RE-VALIDATION THAT LANDS INVALID IS STILL A SUCCESSFUL CALL. The
// connection is kept, the failure is recorded, and the tenant is told plainly.
// It is never deleted for being broken, and this drawer never treats a
// non-ACTIVE result as a rejected submit.

export const RevalidateDrawer = (
    {
        connection,
        opened,
        onClose,
        ...rest
    }: DrawerProps & {
        connection: ProviderConnection,
    },
) => {

    const rotateMutation = useRotateProviderCredentialMutation();
    const revalidateMutation = useRevalidateProviderConnectionMutation();

    const isAssumedRole = connection.credentialType === 'ASSUMED_ROLE';

    const handleSubmit = async (values: RevalidateFormValues) => {

        try {
            // The branch is on the connection's OWN credential type, never on
            // which input happens to be non-empty. A re-validate cannot change
            // the credential SHAPE any more than it can change the provider.
            const updated = isAssumedRole
                ? await revalidateMutation.mutateAsync({id: connection.id})
                : await rotateMutation.mutateAsync(
                    buildRotateInput(
                        connection.id,
                        {apiToken: values.apiToken},
                        values.writable,
                    ),
                );

            // ⚠️ THE MESSAGE REPORTS THE OUTCOME, NOT THE ACTION. "Credential
            // updated" next to a still-INVALID badge reads as the system
            // contradicting itself; the tenant needs to know whether the thing
            // they came to fix is actually fixed.
            //
            // The not-ACTIVE wording also has to be honest about which act just
            // happened: nothing was saved on the ASSUMED_ROLE path, so telling
            // that tenant their "credential was saved" would describe a write
            // that did not occur.
            if (updated?.status === 'ACTIVE') {
                showSuccessNotification(productCopy('Connection validated. {product} can now reach this provider.'));
            } else if (updated?.status === 'UNREACHABLE') {
                showSuccessNotification(isAssumedRole
                    ? I18n.get('The provider could not be reached to check this connection. This is worth retrying shortly.')
                    : I18n.get('Credential saved, but the provider could not be reached to check it. This is worth retrying shortly.'));
            } else {
                showSuccessNotification(isAssumedRole
                    ? I18n.get('The provider still rejected this connection. Check that the external ID is in your role\'s trust policy exactly as shown.')
                    : I18n.get('Credential saved, but the provider still rejected it. Check that the external ID is in your role\'s trust policy exactly as shown.'));
            }

            onClose();

        } catch (e) {
            // The CALL failed. The drawer stays open with the tenant's input.
            showErrorNotifications(e);
            throw e;
        }
    };

    return (
        <Drawer
            transitionProps={{
                transition: 'slide-up',
                duration: 750,
                timingFunction: 'ease',
            }}
            position={'right'}
            offset={10}
            opened={opened}
            onClose={onClose}
            title={I18n.get('Re-validate connection')}
            padding={'xl'}
            size={'xl'}
            scrollAreaComponent={ScrollArea.Autosize}
            {...rest}
        >
            <RevalidateForm
                connection={connection}
                handleSubmit={handleSubmit}
                handleCancel={onClose}
            />
        </Drawer>
    );
};
